// Trades table for the per-game detail page. Lists every analytics trade
// row that matches the toolbar filters (venue + selected team), with
// wallet attribution and polygonscan links on Poly chain rows.
//
// "Bet" column shows the actual yes/no/team as recorded on the venue's
// ticker. Toolbar team selection still filters which trades appear (via
// projectTradeForTeam) so rows map 1:1 to chart bubbles.
//
// Wallet columns: maker and taker shown side-by-side for troubleshooting.
// On Polymarket, `taker_address` is often the operator address (off-chain
// matched fills), while `maker_address` is more reliably a real user proxy
// (signed limit order). Kalshi rows have no counterparty exposed and both
// cells render as "—".

import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, ExternalLink, Filter } from 'lucide-react';
import { SortableTh } from '@/components/atoms/SortableTh';
import type { TradeRow } from '@/services/hydraAnalytics';
import type { GameRow } from '@/views/ValueDashboard/types';
import {
  earliestEventStart,
  projectTradeForTeam,
  tickerNamedTeam,
  tradeUsd,
  type Venue,
} from './helpers';

type SortCol = 'time' | 'size' | 'usd';
type SortDir = 'asc' | 'desc';

interface Props {
  trades: TradeRow[];
  game: GameRow | null;
  selectedTeam: string;
  bothTeams: readonly [string, string];
  /** What slice of trades the parent currently has loaded — drives the
   *  EVENT/ALL toggle highlight. */
  dataMode: 'event' | 'all';
  /** Tell the parent to switch the load extent. ALL triggers a fuller
   *  fetch (deep pre-game tail); EVENT trims back to the event window. */
  onDataModeChange: (mode: 'event' | 'all') => void;
  /** When set (spread/total mode), filter trades to ones whose ticker
   *  matches the selected line — replaces the moneyline team-projection
   *  filter. Null in moneyline mode (team-based logic takes over). */
  lineTickers?: { kalshi: string | null; polymarket: string | null } | null;
  /** Selected line's display label (e.g. "BOS -3", "9.5"). Used as
   *  the prefix in the Bet column for spread/total rows since
   *  tickerNamedTeam can't parse those tickers and the column would
   *  otherwise read "?". Ignored in moneyline mode. */
  lineLabel?: string | null;
}

const PAGE_SIZE = 100;

function polygonscanAddressUrl(addr: string): string {
  return `https://polygonscan.com/address/${addr}`;
}

function polygonscanTxUrl(hash: string): string {
  return `https://polygonscan.com/tx/${hash}`;
}

function shortenAddr(addr: string): string {
  if (!addr) return '';
  if (addr.startsWith('0x') && addr.length > 10) {
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
  }
  return addr.length > 14 ? `${addr.slice(0, 10)}…` : addr;
}

function formatTs(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' };
  const date = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  return { date, time };
}

// Format a ms timestamp to the value shape `<input type="datetime-local">`
// expects: "YYYY-MM-DDTHH:MM" in the browser's local time (matches how
// new Date(localString) parses back).
function msToLocalDatetimeInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// CSV cell escape per RFC 4180: wrap in quotes when the value contains a
// comma, quote, or newline; double-up embedded quotes.
function csvCell(v: string | number | null | undefined): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function nowStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function formatUsd(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(2)}k`;
  return `$${v.toFixed(2)}`;
}

// Shared style for the EVENT/ALL toolbar buttons. Mirrors the chart's
// view-jump buttons (PriceChart.tsx:viewButtonClass) so the two
// surfaces look like the same control.
function dataModeButtonClass(active: boolean): string {
  const base =
    'rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors';
  return active
    ? `${base} border-cyan-400 bg-cyan-500/30 text-cyan-100 hover:bg-cyan-500/40`
    : `${base} border-white/15 bg-white/5 text-white/70 hover:bg-white/10`;
}

// Polymarket-only. Kalshi never exposes counterparty info.
// Maker = limit-order signer (usually a real user proxy).
// Taker = address that filled the order — often a Polymarket operator
// when matched off-chain, not the actual user who hit the book. See
// `hydra/src/upstream/polygon/client.ts` + CTF Exchange V2 ABI.
function pickWallets(row: TradeRow): {
  maker: string | null;
  taker: string | null;
  tx: string | null;
} | null {
  if ((row.source ?? '').toLowerCase() !== 'polymarket') return null;
  const maker = row.maker_address ?? null;
  const taker = row.taker_address ?? null;
  if (!maker && !taker) return null;
  return { maker, taker, tx: row.tx_hash };
}

export function TradesTable({
  trades,
  game,
  selectedTeam,
  bothTeams,
  dataMode,
  onDataModeChange,
  lineTickers = null,
  lineLabel = null,
}: Props) {
  const isLineMode = lineTickers != null;
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState<SortCol>('time');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Per-table source filter — independent of the chart toolbar's
  // enabledVenues, so toggling Kalshi/Poly on the chart doesn't reshape
  // the table (and vice versa). Both on by default.
  const [tableSources, setTableSources] = useState<ReadonlySet<Venue>>(
    () => new Set<Venue>(['kalshi', 'polymarket']),
  );
  const [sourceFilterOpen, setSourceFilterOpen] = useState(false);
  const sourceFilterRef = useRef<HTMLDivElement>(null);

  // Per-table Bet filter — 4 buckets (each team × yes/no). Keyed by
  // `${TEAM}|${side}` so the set survives renames cleanly. Resets to
  // all-on when bothTeams changes (different game).
  const allBetKeys = useMemo<readonly string[]>(
    () => [
      `${bothTeams[0]}|yes`,
      `${bothTeams[0]}|no`,
      `${bothTeams[1]}|yes`,
      `${bothTeams[1]}|no`,
    ],
    [bothTeams],
  );
  const [tableBets, setTableBets] = useState<ReadonlySet<string>>(
    () => new Set(allBetKeys),
  );
  useEffect(() => {
    setTableBets(new Set(allBetKeys));
  }, [allBetKeys]);
  const [betFilterOpen, setBetFilterOpen] = useState(false);
  const betFilterRef = useRef<HTMLDivElement>(null);

  // Seed the time-range inputs from the first/last trade timestamps —
  // i.e. when betting opened and when it closed — until the user takes
  // control. Re-runs as pagination loads more trades, so the bounds
  // widen automatically until the user touches an input (or the dataset
  // finishes loading), at which point timeRangeTouchedRef pins the
  // user's choice.
  useEffect(() => {
    if (timeRangeTouchedRef.current) return;
    if (trades.length === 0) return;
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const t of trades) {
      const ms = Date.parse(t.block_ts ?? t.ts);
      if (!Number.isFinite(ms)) continue;
      if (ms < minMs) minMs = ms;
      if (ms > maxMs) maxMs = ms;
    }
    if (Number.isFinite(minMs) && Number.isFinite(maxMs) && minMs < maxMs) {
      setTimeFrom(msToLocalDatetimeInput(minMs));
      setTimeTo(msToLocalDatetimeInput(maxMs));
    }
  }, [trades]);

  // Dismiss the source-filter popover on outside click.
  useEffect(() => {
    if (!sourceFilterOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const node = sourceFilterRef.current;
      if (node && !node.contains(e.target as Node)) {
        setSourceFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [sourceFilterOpen]);

  useEffect(() => {
    if (!betFilterOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const node = betFilterRef.current;
      if (node && !node.contains(e.target as Node)) {
        setBetFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [betFilterOpen]);

  const toggleSource = (v: Venue) => {
    setTableSources((prev) => {
      const next = new Set(prev);
      if (next.has(v)) {
        if (next.size > 1) next.delete(v); // keep at least one on
      } else {
        next.add(v);
      }
      return next;
    });
    setPage(0);
  };

  const toggleBet = (key: string) => {
    setTableBets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key); // keep at least one on
      } else {
        next.add(key);
      }
      return next;
    });
    setPage(0);
  };
  // `datetime-local` values: "YYYY-MM-DDTHH:MM", interpreted by
  // `new Date()` as local time — directly comparable to Date.parse(row.ts)
  // since both end up as ms-since-epoch. Pre-filled from the trade range
  // on first load (see effect below) so the table opens on the actual
  // betting window instead of "show everything."
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const timeRangeTouchedRef = useRef(false);
  const markTimeRangeTouched = () => {
    timeRangeTouchedRef.current = true;
  };

  const handleSort = (key: string) => {
    const col = key as SortCol;
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      // First click on a new column → descending (biggest/newest first
      // is almost always what you want when sorting trades).
      setSortDir('desc');
    }
    setPage(0);
  };

  // Filter to the toolbar selection + time-range filter, pre-compute the
  // effective-side projection per row, then sort by the active column.
  const filtered = useMemo(() => {
    const fromMs = timeFrom ? new Date(timeFrom).getTime() : null;
    const toMs = timeTo ? new Date(timeTo).getTime() : null;
    const validFrom = fromMs != null && Number.isFinite(fromMs) ? fromMs : null;
    const validTo = toMs != null && Number.isFinite(toMs) ? toMs : null;

    const rows: Array<{
      row: TradeRow;
      rawTeam: string | null;
      tsMs: number;
      usd: number;
    }> = [];
    for (const row of trades) {
      const venue: Venue =
        (row.source ?? '').toLowerCase() === 'polymarket'
          ? 'polymarket'
          : 'kalshi';
      if (!tableSources.has(venue)) continue;
      // Spread/total: strict per-venue ticker filter. Each chip in the
      // line scroller points to exactly one ticker per venue (or null
      // if that venue doesn't have a market at the line), so exclude
      // anything that doesn't match. Moneyline team-projection helper
      // doesn't apply (spread/total tickers don't encode a YES-team
      // code we can parse). Bet-filter (4 team×side buckets) is moot
      // here too — leave as no-op pass-through.
      if (isLineMode) {
        const k = lineTickers!.kalshi;
        const p = lineTickers!.polymarket;
        if (venue === 'kalshi') {
          if (k == null || row.ticker !== k) continue;
        } else {
          if (p == null || row.ticker !== p) continue;
        }
        const tsMs = Date.parse(row.block_ts ?? row.ts);
        if (validFrom != null && Number.isFinite(tsMs) && tsMs < validFrom) continue;
        if (validTo != null && Number.isFinite(tsMs) && tsMs > validTo) continue;
        rows.push({ row, rawTeam: null, tsMs, usd: tradeUsd(row) });
        continue;
      }
      const proj = projectTradeForTeam(row, selectedTeam, bothTeams);
      if (!proj) continue;
      const tsMs = Date.parse(row.block_ts ?? row.ts);
      if (validFrom != null && Number.isFinite(tsMs) && tsMs < validFrom) continue;
      if (validTo != null && Number.isFinite(tsMs) && tsMs > validTo) continue;
      const rawTeam = tickerNamedTeam(row.ticker, row.source, bothTeams);
      // Bet-filter check: only applies when we can identify the named
      // team. Null-team rows can't be bucketed into the 4 options, so
      // they pass through and surface for inspection.
      if (rawTeam) {
        const betKey = `${rawTeam}|${(row.side ?? '').toLowerCase()}`;
        if (!tableBets.has(betKey)) continue;
      }
      rows.push({
        row,
        rawTeam,
        tsMs,
        usd: tradeUsd(row),
      });
    }
    const sign = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sortCol === 'size') return (a.row.size - b.row.size) * sign;
      if (sortCol === 'usd') return (a.usd - b.usd) * sign;
      return (a.tsMs - b.tsMs) * sign;
    });
    return rows;
  }, [trades, selectedTeam, bothTeams, tableSources, tableBets, sortCol, sortDir, timeFrom, timeTo, isLineMode, lineTickers]);

  // Reset to page 0 when the filter shrinks the dataset.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageRows = useMemo(
    () => filtered.slice(clampedPage * PAGE_SIZE, (clampedPage + 1) * PAGE_SIZE),
    [filtered, clampedPage],
  );

  const hasTimeFilter = Boolean(timeFrom || timeTo);

  // Event-window jump: kickoff (Kalshi ticker start) → last trade. Null
  // when we don't have a parseable Kalshi start time yet, in which case
  // the button stays hidden.
  const eventWindow = useMemo(() => {
    const startMs = earliestEventStart(trades);
    if (startMs == null) return null;
    let maxMs = -Infinity;
    for (const t of trades) {
      const ms = Date.parse(t.block_ts ?? t.ts);
      if (Number.isFinite(ms) && ms > maxMs) maxMs = ms;
    }
    if (!(startMs < maxMs)) return null;
    return { startMs, endMs: maxMs };
  }, [trades]);

  // Export the filtered + sorted set (NOT just the visible page) as CSV.
  // Column order mirrors the on-screen layout, plus tx_hash and
  // external_trade_id which are useful for troubleshooting but live
  // outside the table cells.
  const handleExport = () => {
    if (filtered.length === 0) return;
    const header = [
      'ts',
      'block_ts',
      'source',
      'ticker',
      'bet_team',
      'bet_side',
      'price_cents',
      'size',
      'usd',
      'maker_address',
      'taker_address',
      'tx_hash',
      'external_trade_id',
    ];
    const lines = [header.join(',')];
    for (const { row, rawTeam, usd } of filtered) {
      lines.push(
        [
          csvCell(row.ts),
          csvCell(row.block_ts ?? ''),
          csvCell((row.source ?? '').toLowerCase()),
          csvCell(row.ticker),
          csvCell(rawTeam ?? ''),
          csvCell((row.side ?? '').toLowerCase()),
          csvCell(row.price),
          csvCell(row.size),
          csvCell(usd.toFixed(4)),
          csvCell(row.maker_address ?? ''),
          csvCell(row.taker_address ?? ''),
          csvCell(row.tx_hash ?? ''),
          csvCell(row.external_trade_id ?? ''),
        ].join(','),
      );
    }
    // BOM so Excel reads UTF-8 cleanly; \r\n line endings for the same
    // reason. The file content is otherwise plain RFC 4180.
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades_${bothTeams[0]}_${bothTeams[1]}_${nowStamp()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="overflow-x-auto">
      <div className="border-border/40 flex flex-wrap items-center gap-2 border-b px-3 py-2 text-[10px]">
        <span className="text-muted-foreground uppercase tracking-wider">
          Time range
        </span>
        <input
          type="datetime-local"
          step="60"
          value={timeFrom}
          onChange={(e) => {
            markTimeRangeTouched();
            setTimeFrom(e.target.value);
            setPage(0);
          }}
          className="border-border/40 bg-muted/20 rounded border px-1.5 py-0.5 font-mono"
        />
        <span className="text-muted-foreground">→</span>
        <input
          type="datetime-local"
          step="60"
          value={timeTo}
          onChange={(e) => {
            markTimeRangeTouched();
            setTimeTo(e.target.value);
            setPage(0);
          }}
          className="border-border/40 bg-muted/20 rounded border px-1.5 py-0.5 font-mono"
        />
        <button
          type="button"
          onClick={() => {
            markTimeRangeTouched();
            if (eventWindow) {
              setTimeFrom(msToLocalDatetimeInput(eventWindow.startMs));
              setTimeTo(msToLocalDatetimeInput(eventWindow.endMs));
            }
            setPage(0);
            onDataModeChange('event');
          }}
          title="Trim load + view to the event window (kickoff → last trade)"
          className={dataModeButtonClass(dataMode === 'event')}
        >
          Event
        </button>
        <button
          type="button"
          onClick={() => {
            markTimeRangeTouched();
            setTimeFrom('');
            setTimeTo('');
            setPage(0);
            onDataModeChange('all');
          }}
          title="Load every trade hydra has for this game (deep pre-game tail)"
          className={dataModeButtonClass(dataMode === 'all')}
        >
          All
        </button>
        <button
          type="button"
          onClick={handleExport}
          disabled={filtered.length === 0}
          title={
            filtered.length === 0
              ? 'No rows to export'
              : `Export ${filtered.length.toLocaleString()} filtered rows to CSV`
          }
          className="border-border/40 hover:bg-muted/40 disabled:opacity-30 ml-auto inline-flex items-center gap-1 rounded border px-2 py-0.5 uppercase tracking-wider"
        >
          <Download className="h-3 w-3" />
          Export CSV
        </button>
      </div>
      {filtered.length === 0 ? (
        <div className="text-muted-foreground px-3 py-6 text-center text-xs">
          {hasTimeFilter
            ? 'No trades match the current toolbar and time range.'
            : 'No trades match the current toolbar.'}
        </div>
      ) : (
      <>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground border-border/60 border-b text-[10px] uppercase tracking-wider">
            <SortableTh
              sortKey="time"
              activeSortKey={sortCol}
              activeSortDir={sortDir}
              onSort={handleSort}
              className="px-3 py-2 text-[10px] font-normal tracking-wider"
            >
              Time
            </SortableTh>
            <th
              className="px-3 py-2 text-left"
              title="On-chain block timestamp from hydra (Polymarket only). NULL on legacy rows ingested before the 2026-05-15 block_ts deploy and on Kalshi (off-chain)."
            >
              Block TS
            </th>
            <th className="relative px-3 py-2 text-left">
              <div
                ref={sourceFilterRef}
                className="inline-flex items-center gap-1"
              >
                Source
                <button
                  type="button"
                  onClick={() => setSourceFilterOpen((o) => !o)}
                  className="hover:text-foreground rounded p-0.5"
                  title="Filter by source"
                  aria-label="Filter by source"
                >
                  <Filter
                    className={`h-3 w-3 ${
                      tableSources.size < 2 ? 'text-cyan-400' : 'opacity-40'
                    }`}
                  />
                </button>
                {sourceFilterOpen && (
                  <div className="bg-popover text-popover-foreground border-border absolute left-0 top-full z-20 mt-1 flex flex-col gap-1 rounded border p-2 text-[11px] normal-case tracking-normal shadow-lg">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tableSources.has('kalshi')}
                        onChange={() => toggleSource('kalshi')}
                      />
                      Kalshi
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tableSources.has('polymarket')}
                        onChange={() => toggleSource('polymarket')}
                      />
                      Polymarket
                    </label>
                  </div>
                )}
              </div>
            </th>
            <th className="relative px-3 py-2 text-left">
              <div
                ref={betFilterRef}
                className="inline-flex items-center gap-1"
              >
                Bet
                <button
                  type="button"
                  onClick={() => setBetFilterOpen((o) => !o)}
                  className="hover:text-foreground rounded p-0.5"
                  title="Filter by bet"
                  aria-label="Filter by bet"
                >
                  <Filter
                    className={`h-3 w-3 ${
                      tableBets.size < allBetKeys.length
                        ? 'text-cyan-400'
                        : 'opacity-40'
                    }`}
                  />
                </button>
                {betFilterOpen && (
                  <div className="bg-popover text-popover-foreground border-border absolute left-0 top-full z-20 mt-1 flex flex-col gap-1 rounded border p-2 text-[11px] normal-case tracking-normal shadow-lg">
                    {allBetKeys.map((key) => {
                      const [team, side] = key.split('|') as [
                        string,
                        'yes' | 'no',
                      ];
                      return (
                        <label
                          key={key}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <input
                            type="checkbox"
                            checked={tableBets.has(key)}
                            onChange={() => toggleBet(key)}
                          />
                          <SideBadge team={team} side={side} />
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </th>
            <th className="px-3 py-2 text-right">Price</th>
            <SortableTh
              sortKey="size"
              activeSortKey={sortCol}
              activeSortDir={sortDir}
              onSort={handleSort}
              className="px-3 py-2 text-right text-[10px] font-normal tracking-wider"
            >
              Size
            </SortableTh>
            <SortableTh
              sortKey="usd"
              activeSortKey={sortCol}
              activeSortDir={sortDir}
              onSort={handleSort}
              className="px-3 py-2 text-right text-[10px] font-normal tracking-wider"
            >
              USD
            </SortableTh>
            <th className="px-3 py-2 text-left">Maker</th>
            <th className="px-3 py-2 text-left">Taker</th>
            <th className="px-3 py-2 text-left">Ticker</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map(({ row, rawTeam }, idx) => {
            const { date, time } = formatTs(row.block_ts ?? row.ts);
            const blockTs = row.block_ts ? formatTs(row.block_ts) : null;
            const source = (row.source ?? '').toLowerCase();
            const wallets = pickWallets(row);
            const rawSide = (row.side ?? '').toLowerCase();
            const usd = tradeUsd(row);
            const key = row.external_trade_id ?? `${row.ts}-${idx}`;
            return (
              <tr
                key={key}
                className="border-border/30 hover:bg-muted/10 border-b last:border-0"
              >
                <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5 font-mono">
                  <span>{date}</span>{' '}
                  <span className="opacity-70">{time}</span>
                </td>
                <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5 font-mono">
                  {blockTs ? (
                    <>
                      <span>{blockTs.date}</span>{' '}
                      <span className="opacity-70">{blockTs.time}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground/40">—</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      source === 'polymarket'
                        ? 'bg-purple-500/15 text-purple-400'
                        : 'bg-cyan-500/15 text-cyan-400'
                    }`}
                  >
                    {source === 'polymarket' ? 'POLY' : 'KAL'}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <SideBadge
                    team={isLineMode ? (lineLabel ?? '?') : (rawTeam ?? '?')}
                    side={rawSide}
                  />
                </td>
                <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
                  {row.price}¢
                </td>
                <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
                  {row.size}
                </td>
                <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
                  {formatUsd(usd)}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <WalletCell
                    addr={wallets?.maker ?? null}
                    tx={null}
                  />
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <WalletCell
                    addr={wallets?.taker ?? null}
                    tx={wallets?.tx ?? null}
                  />
                </td>
                <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5 font-mono text-[10px]">
                  {row.ticker}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pageCount > 1 && (
        <div className="border-border/60 text-muted-foreground flex items-center justify-between border-t px-3 py-2 text-[10px]">
          <div>
            page {clampedPage + 1} of {pageCount} · {filtered.length} trades
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={clampedPage === 0}
              className="hover:text-foreground hover:bg-muted/40 disabled:opacity-30 rounded px-2 py-0.5"
            >
              Prev
            </button>
            <button
              onClick={() =>
                setPage((p) => Math.min(pageCount - 1, p + 1))
              }
              disabled={clampedPage >= pageCount - 1}
              className="hover:text-foreground hover:bg-muted/40 disabled:opacity-30 rounded px-2 py-0.5"
            >
              Next
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function WalletCell({ addr, tx }: { addr: string | null; tx: string | null }) {
  if (!addr) {
    return <span className="text-muted-foreground/60">—</span>;
  }
  return (
    <>
      <a
        href={polygonscanAddressUrl(addr)}
        target="_blank"
        rel="noopener noreferrer"
        title={addr}
        className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 font-mono"
      >
        {shortenAddr(addr)}
        <ExternalLink className="h-3 w-3" />
      </a>
      {tx && (
        <a
          href={polygonscanTxUrl(tx)}
          target="_blank"
          rel="noopener noreferrer"
          title={`tx ${tx}`}
          className="text-muted-foreground hover:text-foreground ml-2 text-[10px] underline decoration-dotted"
        >
          tx
        </a>
      )}
    </>
  );
}

function SideBadge({ team, side }: { team: string; side: string }) {
  const yes = side === 'yes';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
        yes
          ? 'bg-green-500/15 text-green-400'
          : 'bg-red-500/15 text-red-400'
      }`}
    >
      {team} {side.toUpperCase()}
    </span>
  );
}
